import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./asset/examinationContent.module.scss";
import ChatBot from "./ChatBot/ChatBot";
import CheckpointModal from "./CheckpointModal/CheckpointModal";
import { updateRecordAndGetNextQuestion } from "../../reduxStore/actions/recordActionsHelper";
import { questionLoading$ } from "../../reduxStore/selectors/questionSelectors";
import { questionData$ } from "../../reduxStore/selectors/questionSelectors";
import {
    recordLoading$,
    recordData$,
} from "../../reduxStore/selectors/recordSelectors";
import { usersData$ } from "../../reduxStore/selectors/userSelectors";

const PRACTICE_QUEUE_STORAGE_KEY = "practice_module_question_queue";
const PRACTICE_QUEUE_UPDATED_EVENT = "practice-module-queue-updated";

const getQueueCountFromStorage = () => {
    try {
        const queueRaw = localStorage.getItem(PRACTICE_QUEUE_STORAGE_KEY);
        const parsedQueue = queueRaw ? JSON.parse(queueRaw) : [];
        return Array.isArray(parsedQueue) ? parsedQueue.length : 0;
    } catch (error) {
        console.error("Failed to parse practice question queue count:", error);
        return 0;
    }
};

const ExaminationContent = ({ question, section, questionNumberOverride = null, maxQuestionsInSet = null, hideNextButton = false, is25QuestionMode = false, onCheckpointReached = null, sessionStartTime = null }) => {
    const dispatch = useDispatch();
    const isQuestionLoading = useSelector(questionLoading$);
    const questionResponseData = useSelector(questionData$);
    const isRecordLoading = useSelector(recordLoading$);
    const currentRecord = useSelector(recordData$);
    const userData = useSelector(usersData$);

    // Combined loading state
    const isLoading = isQuestionLoading || isRecordLoading;

    // State for answer selection
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [confirmedAnswer, setConfirmedAnswer] = useState(null);
    const [sprAnswer, setSprAnswer] = useState("");
    const [showConfirmButton, setShowConfirmButton] = useState(false);
    const [questionStartTime, setQuestionStartTime] = useState(null);
    const [currentQuestionId, setCurrentQuestionId] = useState(null);
    const [showCheckpoint, setShowCheckpoint] = useState(false);
    const [checkpointStats, setCheckpointStats] = useState({ timeSpent: 0 });
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [queuedQuestionCount, setQueuedQuestionCount] = useState(() => getQueueCountFromStorage());
    const [showQueueFallbackNotice, setShowQueueFallbackNotice] = useState(false);
    const [lastFallbackNoticeId, setLastFallbackNoticeId] = useState(null);
    const lastNextClickAtRef = useRef(0);

    const pendingQuestionKey = currentRecord?._id
        ? `practice_pending_question_${currentRecord._id}`
        : null;

    const queuedQuestionNotFoundId = questionResponseData?.notFoundQueuedQuestionId || null;
    const answeredCount = (currentRecord?.totalQuestions || 0) + (confirmedAnswer ? 1 : 0);

    useEffect(() => {
        const syncQueueCount = () => {
            setQueuedQuestionCount(getQueueCountFromStorage());
        };

        const handleQueueUpdated = (event) => {
            const nextCount = event?.detail?.queueCount;
            if (typeof nextCount === "number") {
                setQueuedQuestionCount(nextCount);
                return;
            }
            syncQueueCount();
        };

        window.addEventListener("storage", syncQueueCount);
        window.addEventListener(PRACTICE_QUEUE_UPDATED_EVENT, handleQueueUpdated);

        return () => {
            window.removeEventListener("storage", syncQueueCount);
            window.removeEventListener(PRACTICE_QUEUE_UPDATED_EVENT, handleQueueUpdated);
        };
    }, []);

    useEffect(() => {
        if (!queuedQuestionNotFoundId) {
            return;
        }

        if (queuedQuestionNotFoundId !== lastFallbackNoticeId) {
            setShowQueueFallbackNotice(true);
            setLastFallbackNoticeId(queuedQuestionNotFoundId);
        }
    }, [queuedQuestionNotFoundId, lastFallbackNoticeId]);

    // Track when question starts - only reset states when question actually changes
    useEffect(() => {
        if (question?._id && question._id !== currentQuestionId) {
            setCurrentQuestionId(question._id);
            setSelectedAnswer(null);
            setConfirmedAnswer(null);
            setSprAnswer("");
            setShowConfirmButton(false);
            setQuestionStartTime(Date.now());
            setImageLoading(true);
            setImageError(false);
        }
    }, [question?._id, currentQuestionId]);

    // Get current question data
    const getCurrentQuestion = () => {
        if (!question) {
            return null;
        }

        // Convert MongoDB Map objects to JavaScript Maps for frontend use
        const convertToMap = (obj) => {
            if (!obj) return new Map();
            if (obj instanceof Map) return obj;
            if (typeof obj === "object") {
                return new Map(Object.entries(obj));
            }
            return new Map();
        };

        // Handle single question object - mapping MongoDB schema
        return {
            questionText: question.question || "",
            section: question.section || "",
            choices: convertToMap(question.choices),
            correctAnswer: question.correctAnswer || "",
            rationales: convertToMap(question.rationales),
            hint: question.hint || "",
            questionType: question.questionType || "MCQ",
            difficulty: question.difficulty || "",
            media: question.media || null,
        };
    };

    const currentQuestionData = getCurrentQuestion();

    // Handle answer selection for MCQ
    const handleAnswerSelect = (answer) => {
        if (confirmedAnswer) {
            return;
        }
        setSelectedAnswer(answer);
        setShowConfirmButton(true);
    };

    // Handle SPR input change
    const handleSprInputChange = (event) => {
        if (confirmedAnswer) {
            return;
        }
        const value = event.target.value;
        setSprAnswer(value);
        setShowConfirmButton(value.trim().length > 0);
    };

    // Handle answer confirmation
    const handleConfirmAnswer = () => {
        if (confirmedAnswer) {
            return;
        }

        if (currentQuestionData?.questionType === "SPR") {
            if (sprAnswer.trim()) {
                setConfirmedAnswer(sprAnswer.trim());
                setShowConfirmButton(false);
                if (pendingQuestionKey) {
                    sessionStorage.removeItem(pendingQuestionKey);
                }
            }
        } else {
            // MCQ
            if (selectedAnswer) {
                setConfirmedAnswer(selectedAnswer);
                setShowConfirmButton(false);
                if (pendingQuestionKey) {
                    sessionStorage.removeItem(pendingQuestionKey);
                }
            }
        }
    };

    useEffect(() => {
        if (!pendingQuestionKey || !question?._id) {
            return;
        }

        if (!confirmedAnswer) {
            sessionStorage.setItem(pendingQuestionKey, question._id);
        }
    }, [question?._id, pendingQuestionKey, confirmedAnswer]);

    // Handle next question
    const handleNextQuestion = () => {
        const now = Date.now();
        if (now - lastNextClickAtRef.current < 700) {
            return;
        }
        lastNextClickAtRef.current = now;

        if (confirmedAnswer) {
            // Update record with answer data before moving to next question
            if (currentRecord && currentRecord._id && question && userData?.id) {
                let userAnswer = confirmedAnswer;
                let isCorrect = false;

                // Calculate if answer is correct
                if (currentQuestionData?.questionType === "SPR") {
                    isCorrect =
                        userAnswer.toLowerCase() ===
                        String(currentQuestionData.correctAnswer || "")
                            .toLowerCase()
                            .trim();
                } else {
                    // MCQ
                    isCorrect =
                        String(userAnswer) ===
                        String(currentQuestionData.correctAnswer || "").trim();
                }

                const timeSpent = questionStartTime
                    ? Math.floor((Date.now() - questionStartTime) / 1000)
                    : 0;

                // Create the new answered question
                const newAnsweredQuestion = {
                    questionId: question._id,
                    isCorrect: isCorrect,
                    difficulty: currentQuestionData.difficulty || "medium",
                    timeSpent: timeSpent,
                    answeredAt: new Date().toISOString(),
                    userAnswer: userAnswer,
                };

                // Create updated record data
                const updatedAnsweredQuestions = [
                    ...(currentRecord.answeredQuestions || []),
                    newAnsweredQuestion,
                ];
                const newTotalQuestions = (currentRecord.totalQuestions || 0) + 1;
                const newCorrectAnswers =
                    (currentRecord.correctAnswers || 0) + (isCorrect ? 1 : 0);
                const newCurrentQuestionIndex =
                    (currentRecord.currentQuestionIndex || 0) + 1;

                dispatch(
                    updateRecordAndGetNextQuestion.request({
                        recordUpdate: {
                            id: currentRecord._id,
                            data: {
                                answeredQuestions: updatedAnsweredQuestions,
                                totalQuestions: newTotalQuestions,
                                correctAnswers: newCorrectAnswers,
                                currentQuestionIndex: newCurrentQuestionIndex,
                                currentQuestionId: null,
                            },
                        },
                        questionRequest: {
                            section,
                            userId: userData.id,
                            queuedQuestionId: (() => {
                                try {
                                    const queueRaw = localStorage.getItem("practice_module_question_queue");
                                    const parsed = queueRaw ? JSON.parse(queueRaw) : [];
                                    if (Array.isArray(parsed) && parsed.length > 0) {
                                        return parsed[0]?.questionId || null;
                                    }
                                } catch (error) {
                                    console.error("Failed to read practice question queue:", error);
                                }
                                return null;
                            })(),
                        },
                    }),
                );
            } else {
                console.warn("Cannot update record - missing data:", {
                    hasCurrentRecord: !!currentRecord,
                    hasCurrentRecordId: !!(currentRecord && currentRecord._id),
                    hasQuestion: !!question,
                    hasUserId: !!(userData && userData.id),
                });
            }
        }
    };

    // Show error if no question data
    if (!currentQuestionData) {
        return (
            <div className={styles.question}>
                <div className={styles.errorContainer}>
                    <h2>Error: No Question Data</h2>
                    <p>Unable to load question. Please try again.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={styles.contentWrapper}>
                <div className={styles.question}>
                    <div className={styles.frameParent}>
                        <div className={styles.question1Parent}>
                            <div className={styles.question1}>
                                Question #{questionNumberOverride !== null ? questionNumberOverride : (currentRecord.currentQuestionIndex || 0) + 1}
                                {maxQuestionsInSet && ` of ${maxQuestionsInSet}`}
                            </div>
                            {queuedQuestionCount > 0 && (
                                <div className={styles.queueCountBadge}>
                                    Currently {queuedQuestionCount} in question queue
                                </div>
                            )}
                            <div className={styles.questionType}>
                                {currentQuestionData.questionType === "SPR"
                                    ? "Free Response"
                                    : "Multiple Choice"}
                            </div>
                        </div>
                        <div className={styles.component1}>
                            {showQueueFallbackNotice && queuedQuestionNotFoundId && (
                                <div className={styles.queueFallbackOverlay}>
                                    <div className={styles.queueFallbackCard}>
                                        <h3>Queued Question Not Found</h3>
                                        <p>
                                            Question ID <strong>{queuedQuestionNotFoundId}</strong> could not be found.
                                            A fallback question was loaded automatically.
                                        </p>
                                        <button
                                            className={styles.queueFallbackContinueButton}
                                            type="button"
                                            onClick={() => setShowQueueFallbackNotice(false)}
                                        >
                                            Continue
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className={styles.theAftermathOfTheIndustriaParent}>
                                {currentQuestionData.passageText && (
                                    <>
                                        <div className={styles.theAftermathOf}>
                                            "{currentQuestionData.passageText}"
                                        </div>
                                        <img className={styles.frameChild} alt="" src="Line 24.svg" />
                                    </>
                                )}
                                <div className={styles.whichOfTheFollowingBestDeParent}>
                                    <div className={styles.whichOfThe}>
                                        <strong>{currentQuestionData.questionText}</strong>
                                    </div>

                                    {currentQuestionData.media && (
                                        <div className={styles.questionMedia}>
                                            {imageLoading && (
                                                <div className={styles.imageLoader}>
                                                    <div className={styles.imageSpinner}></div>
                                                    <span>Loading image...</span>
                                                </div>
                                            )}
                                            {imageError && (
                                                <div className={styles.imageError}>
                                                    <span>Failed to load image</span>
                                                </div>
                                            )}
                                            <img
                                                src={currentQuestionData.media}
                                                alt="Question visual aid"
                                                className={styles.mediaImage}
                                                style={{ display: imageLoading || imageError ? 'none' : 'block' }}
                                                onLoad={() => setImageLoading(false)}
                                                onError={() => {
                                                    setImageLoading(false);
                                                    setImageError(true);
                                                }}
                                            />
                                        </div>
                                    )}

                                    <div className={styles.awnserParent}>
                                        {currentQuestionData.questionType === "SPR" ? (
                                            // SPR (Student-Produced Response) - Text Input
                                            <div className={styles.sprContainer}>
                                                <textarea
                                                    className={`${styles.sprInput} ${confirmedAnswer ? styles.sprInputDisabled : ""
                                                        }`}
                                                    placeholder="Enter your answer here..."
                                                    value={sprAnswer}
                                                    onChange={handleSprInputChange}
                                                    disabled={confirmedAnswer !== null}
                                                    rows={4}
                                                />
                                                {confirmedAnswer && (
                                                    <div className={styles.sprResult}>
                                                        <div className={styles.sprSubmitted}>
                                                            <strong>Your Answer:</strong> {confirmedAnswer}
                                                        </div>
                                                        {currentQuestionData.correctAnswer && (
                                                            <div className={styles.sprCorrectAnswer}>
                                                                <strong>Correct Answer:</strong>{" "}
                                                                {currentQuestionData.correctAnswer}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            // MCQ (Multiple Choice Questions)
                                            Array.from(currentQuestionData.choices.entries()).map(
                                                ([option, text], index) => {
                                                    // Ensure string comparison for correct answer
                                                    const correctAnswerKey = String(
                                                        currentQuestionData.correctAnswer || "",
                                                    ).trim();
                                                    const currentOptionKey = String(option || "").trim();

                                                    // Show correct answer when user has confirmed
                                                    const isCorrect =
                                                        confirmedAnswer &&
                                                        currentOptionKey === correctAnswerKey;
                                                    // Show wrong answer only for the option the user selected
                                                    const isWrong =
                                                        confirmedAnswer &&
                                                        currentOptionKey ===
                                                        String(confirmedAnswer || "").trim() &&
                                                        currentOptionKey !== correctAnswerKey;

                                                    return (
                                                        <button
                                                            key={option}
                                                            className={`${styles.answerButton} ${selectedAnswer === option
                                                                ? styles.answerButtonSelected
                                                                : ""
                                                                } ${confirmedAnswer === option
                                                                    ? styles.answerButtonConfirmed
                                                                    : ""
                                                                } ${isCorrect ? styles.answerButtonCorrect : ""} ${isWrong ? styles.answerButtonWrong : ""
                                                                } ${confirmedAnswer ? styles.answerButtonDisabled : ""
                                                                }`}
                                                            onClick={() => handleAnswerSelect(option)}
                                                            disabled={confirmedAnswer !== null}
                                                        >
                                                            <div className={styles.radioContainer}>
                                                                <div
                                                                    className={`${styles.radioButton} ${selectedAnswer === option
                                                                        ? styles.radioButtonSelected
                                                                        : ""
                                                                        } ${isCorrect ? styles.radioButtonCorrect : ""} ${isWrong ? styles.radioButtonWrong : ""
                                                                        }`}
                                                                >
                                                                    {(selectedAnswer === option ||
                                                                        isCorrect ||
                                                                        isWrong) && (
                                                                            <div className={styles.radioButtonInner}></div>
                                                                        )}
                                                                </div>
                                                            </div>
                                                            <div className={styles.answerText}>
                                                                {option}. {text}
                                                            </div>
                                                        </button>
                                                    );
                                                },
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.buttonsParent}>
                                <div className={styles.leftButtons}>
                                    {showConfirmButton && (
                                        <button
                                            className={styles.confirmButton}
                                            onClick={handleConfirmAnswer}
                                        >
                                            <svg
                                                className={styles.checkIcon}
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M20 6L9 17l-5-5"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            <span>Confirm Answer</span>
                                        </button>
                                    )}
                                </div>

                                <div className={styles.navigationButtons}>
                                    {!hideNextButton && !(is25QuestionMode && (currentRecord?.totalQuestions + 1) % 25 === 0) && (
                                        <button
                                            className={`${styles.navButton} ${styles.nextButton} ${!confirmedAnswer || isLoading ? styles.navButtonDisabled : ""
                                                }`}
                                            onClick={handleNextQuestion}
                                            disabled={!confirmedAnswer || isLoading}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <div className={styles.buttonSpinner}></div>
                                                    <span>
                                                        {isRecordLoading
                                                            ? "Saving Answer..."
                                                            : "Loading Next Question..."}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>Next Question</span>
                                                    <svg
                                                        className={styles.environmentBulb}
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M5 12h14M12 5l7 7-7 7"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {is25QuestionMode && (currentRecord?.totalQuestions + 1) % 25 === 0 && confirmedAnswer && (
                                        <button
                                            className={`${styles.navButton} ${styles.nextButton}`}
                                            onClick={() => {
                                                const timeSpent = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
                                                setCheckpointStats({ timeSpent });
                                                setShowCheckpoint(true);
                                            }}
                                        >
                                            <span>End Session</span>
                                            <svg
                                                className={styles.environmentBulb}
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M9 5l7 7-7 7"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chatbot Section */}
                <ChatBot
                    questionId={question?._id}
                    confirmedAnswer={confirmedAnswer}
                    is25QuestionMode={is25QuestionMode}
                    answeredCount={answeredCount}
                    questionLimit={25}
                />
            </div>

            {is25QuestionMode && (
                <CheckpointModal
                    isOpen={showCheckpoint}
                    questionsAnswered={(currentRecord?.totalQuestions || 0) + 1}
                    correctAnswers={(currentRecord?.correctAnswers || 0) + (confirmedAnswer ? 1 : 0)}
                    timeSpent={(() => {
                        const seconds = checkpointStats.timeSpent;
                        const mins = Math.floor(seconds / 60);
                        const secs = seconds % 60;
                        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                    })()}
                    onContinue={() => {
                        setShowCheckpoint(false);
                        if (onCheckpointReached) {
                            onCheckpointReached('continue');
                        }
                    }}
                    onEnd={() => {
                        setShowCheckpoint(false);
                        if (onCheckpointReached) {
                            onCheckpointReached('end');
                        }
                    }}
                />
            )}
        </>
    );
};

export default ExaminationContent;
